import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import PricingSection from "./PricingSection";
import "../App.css";
import { trackGoal } from "../utils/datafast";
import useFocusTrap from "../hooks/useFocusTrap";

export default function OnboardingModal({ onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState("free");
  const [subscriptionStatus, setSubscriptionStatus] = useState("inactive");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    publicSlug: "",
  });
  const [completedLink, setCompletedLink] = useState("");
  const [copiedPublicLink, setCopiedPublicLink] = useState(false);
  const copyTimerRef = useRef(null);
  const modalRef = useRef(null);
  const pricingModalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const pricingCloseButtonRef = useRef(null);

  const fallbackOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? `${window.location.origin}/p`
      : "";
  const PUBLIC_BASE =
    process.env.REACT_APP_PUBLIC_BASE_URL ||
    (fallbackOrigin ? fallbackOrigin : "https://www.clearbadge.me/p");

  useEffect(() => {
    const load = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) return;
      const { data } = await supabase
        .from("profiles")
        .select("first_name,last_name,email,phone,company_agency,public_link,public_slug,subscription_tier,subscription_status")
        .eq("id", userId)
        .maybeSingle();

      const cleanedPhone = digitsOnly(data?.phone || "");
      if (data) {
        setSubscriptionTier(data.subscription_tier || "free");
        setSubscriptionStatus(data.subscription_status || "inactive");
        setForm({
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          email: data.email || sessionData?.session?.user?.email || "",
          phone: cleanedPhone,
          company: data.company_agency || "",
          publicSlug: data.public_slug && data.public_slug !== "-" ? data.public_slug : "",
        });
      } else {
        setSubscriptionTier("free");
        setSubscriptionStatus("inactive");
        setForm((prev) => ({
          ...prev,
          email: sessionData?.session?.user?.email || "",
        }));
      }
    };
    load();
  }, []);

  const generatedLink = useMemo(() => {
    const slugParts =
      form.publicSlug ||
      [form.firstName, form.lastName]
        .filter(Boolean)
        .join("-")
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "") ||
      "";
    return slugParts ? `${PUBLIC_BASE}/${slugParts}` : "";
  }, [form.firstName, form.lastName, form.publicSlug, PUBLIC_BASE]);

  const slugify = (str) =>
    str
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const digitsOnly = (val) => (val || "").replace(/\D/g, "");

  const formatPhoneNumber = (raw) => {
    const digits = digitsOnly(raw).slice(0, 15); // cap length to keep formatting sane
    if (!digits) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const randomSuffix = () => crypto.randomUUID().replace(/-/g, "").slice(0, 12);

  // Always append a unique 12-char hash to avoid collisions on same names
  const ensureUniqueSlug = async (baseSlug, userId) => {
    const base = baseSlug || "user";
    const candidate = `${base}-${randomSuffix()}`;
    // Still check for rare collision
    const { data, error } = await supabase
      .from("profiles")
      .select("id")
      .eq("public_slug", candidate)
      .limit(1)
      .maybeSingle();
    if (error) {
      return candidate;
    }
    if (!data || data.id === userId) return candidate;
    // If collision, recursively try again
    return ensureUniqueSlug(base, userId);
  };

  const nextStep = () => {
    if (step === 1 && (!form.firstName || !form.lastName)) {
      setError("Please fill first name and last name.");
      return;
    }
    setError("");
    setStep((s) => s + 1);
  };

  const prevStep = () => {
    setError("");
    setStep((s) => Math.max(1, s - 1));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("Session not found. Please sign in again.");

      const cleanedExisting = form.publicSlug && form.publicSlug !== "-" ? slugify(form.publicSlug) : "";
      const baseSlug = cleanedExisting || slugify([form.firstName, form.lastName].filter(Boolean).join("-")) || "user";
      const uniqueSlug = await ensureUniqueSlug(baseSlug, userId);

      const payload = {
        id: userId,
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        company_agency: form.company,
        public_link: `${PUBLIC_BASE}/${uniqueSlug}`,
        public_slug: uniqueSlug,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from("profiles")
        .upsert([payload], { onConflict: "id", returning: "representation" })
        .select("id, public_slug, first_name, last_name, email, phone, company_agency")
        .maybeSingle();

      if (updateError) {
        console.error("Onboarding save failed:", updateError);
        throw updateError;
      }
      // Always verify the row exists after the upsert (handles 204/minimal responses).
      const { data: verifiedProfile, error: verifyError } = await supabase
        .from("profiles")
        .select("id, public_slug, first_name, last_name, email, phone, company_agency")
        .eq("id", userId)
        .maybeSingle();

      if (verifyError) {
        console.error("Onboarding verify failed:", verifyError);
        throw verifyError;
      }
      if (!verifiedProfile?.id) {
        throw new Error("Profile not saved. Please try again.");
      }

      const fullLink = `${PUBLIC_BASE}/${verifiedProfile.public_slug || uniqueSlug}`;
      setCompletedLink(fullLink);
      setStep(3);
      trackGoal("onboarding_completed", { role: "professional" });
    } catch (err) {
      setError(err.message || "Unable to save profile.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopyPublicLink = async () => {
    const linkToCopy = completedLink || generatedLink;
    if (!linkToCopy) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(linkToCopy);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = linkToCopy;
        textarea.style.position = "fixed";
        textarea.style.top = "-1000px";
        textarea.style.left = "-1000px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      trackGoal("digital_card_shared", { method: "copy" });
      setCopiedPublicLink(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedPublicLink(false), 5000);
    } catch (_err) {
      // no-op
    }
  };

  useFocusTrap({
    isOpen: true,
    containerRef: modalRef,
    onClose,
    initialFocusRef: closeButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: showPricingModal,
    containerRef: pricingModalRef,
    onClose: () => setShowPricingModal(false),
    initialFocusRef: pricingCloseButtonRef,
    lockScroll: true,
  });

  return (
    <div className="modal-backdrop" style={{ zIndex: 200 }}>
      <div
        ref={modalRef}
        className="modal onboarding-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <p className="eyebrow">Welcome to ClearBadge<sup>&trade;</sup></p>
            <h3 id="onboarding-modal-title">Let's set up your profile</h3>
          </div>
          <button ref={closeButtonRef} className="icon-btn" type="button" onClick={onClose} aria-label="Close onboarding">
            x
          </button>
        </div>

        <div className="stepper">
          <div className={`stepper-dot ${step === 1 ? "active" : ""}`}>1</div>
          <div className="stepper-line" />
          <div className={`stepper-dot ${step === 2 ? "active" : ""}`}>2</div>
          <div className="stepper-line" />
          <div className={`stepper-dot ${step === 3 ? "active" : ""}`}>3</div>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

        {step === 1 && (
          <div className="form-grid">
            <div className="form-row dual">
              <div>
                <label htmlFor="onboarding-first-name">First name *</label>
                <input
                  id="onboarding-first-name"
                  type="text"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div>
                <label htmlFor="onboarding-last-name">Last name *</label>
                <input
                  id="onboarding-last-name"
                  type="text"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <label htmlFor="onboarding-phone">Phone</label>
              <input
                id="onboarding-phone"
                type="tel"
                value={formatPhoneNumber(form.phone)}
                onChange={(e) => setForm({ ...form, phone: digitsOnly(e.target.value) })}
                placeholder="(123) 456-7890"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="onboarding-company">Company/Agency</label>
              <input
                id="onboarding-company"
                type="text"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Your company"
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="form-grid">
            <div className="form-row">
              <h4 style={{ marginBottom: 4 }}>Great work!</h4>
              <p className="hint" style={{ marginBottom: 12 }}>
                Now that you've setup your account, you can view your complimentary digital business card at the link below or in the <strong>My Info</strong> page.
              </p>
              <label htmlFor="onboarding-public-link">Your public link</label>
              <div className="link-row">
                <input id="onboarding-public-link" type="text" value={completedLink || generatedLink} readOnly />
                <button
                  type="button"
                  className={`ghost copy-btn ${copiedPublicLink ? "copied" : ""}`}
                  onClick={handleCopyPublicLink}
                >
                  <span className="copy-text" aria-hidden={copiedPublicLink}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="copy-icon">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
                    </svg>
                  </span>
                  <span className="copy-check" aria-hidden={!copiedPublicLink}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="copy-icon">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </span>
                  <span className="sr-only">Copy public link</span>
                </button>
              </div>
              <p className="hint">Share this link as your digital business card.</p>
              <div className="upgrade-callout">
                <h4>Time to list your first license</h4>
                <p className="muted upgrade-callout-subtext">
                  ClearBadge<sup>&trade;</sup> allows users to list their first license 100% free. <br></br> <br></br>Check out how ClearBadge's Pro and Elite tiers are helping licensed professionals become more successful.
                </p>
                <div className="button-row">
                  <button
                    className="ghost"
                    type="button"
                    onClick={() => {
                      trackGoal("upsell_interacted", {
                        upsell_id: "onboarding_pricing_callout",
                        action: "learn_more",
                      });
                      setShowPricingModal(true);
                    }}
                    data-fast-goal="view_pricing"
                    data-fast-goal-source="onboarding"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="button-row" style={{ justifyContent: "space-between", marginTop: 12 }}>
          {step > 1 && step < 3 ? (
            <button className="ghost" type="button" onClick={prevStep}>
              Back
            </button>
          ) : (
            <></>
          )}
          {step === 1 && (
            <>
              <span />
              <button className="primary dark" type="button" onClick={nextStep}>
                Continue
              </button>
            </>
          )}
          {step === 2 && (
            <button className="primary" type="button" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Finish"}
            </button>
          )}
          {step === 3 ? (
            <>
              <button
                className="ghost"
                type="button"
                onClick={() => {
                  onComplete?.();
                  onClose();
                }}
              >
                Close
              </button>
                <button
                  className="primary"
                  type="button"
                  onClick={() => {
                    onComplete?.();
                    onClose();
                    window.location.href = "/licenses?add=1";
                  }}
                >
                  Add my first license
                </button>
            </>
          ) : null}
        </div>
      </div>
      {showPricingModal && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 210 }}
          onClick={() => setShowPricingModal(false)}
        >
          <div
            ref={pricingModalRef}
            className="modal pricing-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboarding-pricing-title"
            style={{ maxWidth: 1200, maxHeight: "80vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pricing-modal-card">
              <h3 id="onboarding-pricing-title" className="sr-only">Pricing plans</h3>
              <button
                ref={pricingCloseButtonRef}
                className="icon-btn pricing-modal-close"
                type="button"
                onClick={() => setShowPricingModal(false)}
                aria-label="Close"
              >
                x
              </button>
              <PricingSection
                id="pricing-modal"
                disableFreeCta
                freeCtaLabel="Current Plan"
                currentTier={subscriptionTier}
                currentStatus={subscriptionStatus}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

